import { useState } from 'react';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import { Select, TextField } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BORDER = 'var(--vo-steel)';
const BORDER_ERR = 'var(--vo-danger)';

const Card = styled.div`
  max-width: 800px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 24px;
`;
const IconTile = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 2px;
`;
const SectionSub = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;
const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  ${({ $full }) => $full && 'grid-column: 1 / -1;'}
`;
const FieldLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${MUTED};
`;
const Required = styled.span`
  color: ${BORDER_ERR};
  margin-left: 2px;
`;
const ErrorMsg = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${BORDER_ERR};
  margin: 2px 0 0;
`;

/* ── Tag chip input ── */
const ChipBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  padding: 8px 12px;
  min-height: 40px;
  cursor: text;
  &:focus-within { border-color: ${BLUE}; }
`;
const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--vo-capri-50);
  color: ${BLUE};
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px 4px 10px;
  border-radius: 20px;
`;
const ChipX = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  color: ${MUTED};
  padding: 0;
  display: flex;
  align-items: center;
  &:hover { color: ${BORDER_ERR}; }
`;
const ChipTextInput = styled.input`
  border: none;
  outline: none;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  flex: 1;
  min-width: 100px;
  background: transparent;
  padding: 0;
`;

const TagInput = ({ value, onChange, placeholder }) => {
  const [inputVal, setInputVal] = useState('');
  const tags = Array.isArray(value) ? value : [];

  const addTag = () => {
    const tag = inputVal.trim();
    if (tag && !tags.includes(tag)) onChange([...tags, tag]);
    setInputVal('');
  };

  const removeTag = (tag) => onChange(tags.filter(t => t !== tag));

  return (
    <ChipBox>
      {tags.map(tag => (
        <Chip key={tag}>
          {tag}
          <ChipX type="button" onClick={() => removeTag(tag)}>×</ChipX>
        </Chip>
      ))}
      <ChipTextInput
        value={inputVal}
        onChange={e => setInputVal(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); }
          if (e.key === 'Backspace' && !inputVal && tags.length) removeTag(tags[tags.length - 1]);
        }}
        placeholder={tags.length === 0 ? placeholder : ''}
      />
    </ChipBox>
  );
};

export const Detalhes = ({ register, control, errors, users, id, t }) => (
  <Card>
    <SectionHead>
      <IconTile>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="12 2 2 7 12 12 22 7 12 2"/>
          <polyline points="2 17 12 22 22 17"/>
          <polyline points="2 12 12 17 22 12"/>
        </svg>
      </IconTile>
      <div>
        <SectionTitle>{t('asm:areas.form.detailsSectionTitle')}</SectionTitle>
        <SectionSub>{t('asm:areas.form.detailsSectionSubtitle')}</SectionSub>
      </div>
    </SectionHead>

    <FormGrid>
      <FieldGroup $full>
        <FieldLabel htmlFor="area-name">
          {t('asm:areas.form.name.label')}<Required>*</Required>
        </FieldLabel>
        <div data-tour="area-name">
          <TextField
            id="area-name"
            fullWidth
            {...register('name', { required: t('asm:areas.form.name.required') })}
            placeholder={t('asm:areas.form.name.placeholder')}
            error={!!errors?.name}
            disabled={!!id}
            autoFocus={!id}
          />
          {errors?.name && <ErrorMsg>{errors.name.message}</ErrorMsg>}
        </div>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>{t('asm:areas.form.criticality.label')}<Required>*</Required></FieldLabel>
        <div data-tour="area-criticity">
          <Controller
            control={control}
            name="criticity"
            rules={{ required: t('asm:areas.form.criticality.required') }}
            render={({ field: { onChange, value } }) => (
              <>
                <Select
                  name="criticity"
                  value={value}
                  onChange={onChange}
                  error={!!errors?.criticity}
                  options={[
                    { value: '', label: t('asm:areas.form.criticality.placeholder') },
                    ...['1', '2', '3', '4', '5'].map(item => ({ value: item, label: t('asm:criticality.' + item) })),
                  ]}
                />
                {errors?.criticity && <ErrorMsg>{errors.criticity.message}</ErrorMsg>}
              </>
            )}
          />
        </div>
      </FieldGroup>

      <FieldGroup>
        <FieldLabel>{t('asm:areas.form.responsible.label')}<Required>*</Required></FieldLabel>
        <div data-tour="area-user">
          <Controller
            control={control}
            name="user"
            rules={{ required: t('asm:areas.form.responsible.required') }}
            render={({ field: { onChange, value } }) => (
              <>
                <Select
                  name="user"
                  value={value}
                  onChange={onChange}
                  error={!!errors?.user}
                  options={[
                    { value: '', label: t('asm:areas.form.responsible.placeholder') },
                    ...(users || []).map(u => ({ value: u.name, label: u.name })),
                  ]}
                />
                {errors?.user && <ErrorMsg>{errors.user.message}</ErrorMsg>}
              </>
            )}
          />
        </div>
      </FieldGroup>

      <FieldGroup $full>
        <FieldLabel>{t('asm:areas.form.tags.label')}</FieldLabel>
        <div data-tour="area-tags">
          <Controller
            control={control}
            name="tags"
            render={({ field: { onChange, value } }) => (
              <TagInput
                value={value}
                onChange={onChange}
                placeholder={t('asm:areas.form.tags.placeholder')}
              />
            )}
          />
        </div>
      </FieldGroup>

      <FieldGroup $full>
        <FieldLabel htmlFor="area-desc">
          {t('asm:areas.form.description.label')}<Required>*</Required>
        </FieldLabel>
        <div data-tour="area-description">
          <TextField
            id="area-desc"
            multiline
            fullWidth
            {...register('description', { required: t('asm:areas.form.description.required') })}
            rows={4}
            placeholder={t('asm:areas.form.description.placeholder')}
            error={!!errors?.description}
          />
          {errors?.description && <ErrorMsg>{errors.description.message}</ErrorMsg>}
        </div>
      </FieldGroup>
    </FormGrid>
  </Card>
);
